import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import CartItem from '../components/CartItem';
import Button from '../components/Button';
import { useI18n } from '../context/I18nContext';

const CartPage = () => {
  const { items, cartTotal, clearCart } = useCart();
  const { t } = useI18n();

  const shipping = cartTotal >= 100 ? 0 : 9.99;
  const tax = cartTotal * 0.08;
  const total = cartTotal + shipping + tax;

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-amber-50 flex flex-col items-center justify-center gap-6 px-4">
        <div className="text-center">
          <span className="text-8xl mb-6 block">🛒</span>
          <h2 className="text-2xl font-bold text-gray-700 mb-3 font-serif">{t('cart.empty')}</h2>
          <p className="text-gray-500 mb-8 max-w-sm">
            {t('cart.emptyDescription')}
          </p>
          <Link to="/products">
            <Button>{t('cart.browse')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-amber-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold font-serif text-amber-900 mb-8">{t('cart.title')}</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600">
                {items.length === 1 ? t('cart.itemCount', { count: items.length }) : t('cart.itemCountPlural', { count: items.length })}
              </span>
              <button
                onClick={clearCart}
                className="text-sm text-red-500 hover:text-red-700 transition-colors"
              >
                {t('cart.clearAll')}
              </button>
            </div>
            {items.map(item => (
              <CartItem key={item.id} item={item} />
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-amber-100 p-6 sticky top-24">
              <h2 className="text-xl font-bold font-serif text-amber-900 mb-6">{t('cart.orderSummary')}</h2>
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>{t('cart.subtotal')}</span>
                  <span className="font-medium">${cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>{t('cart.shipping')}</span>
                  <span className={shipping === 0 ? 'text-green-600 font-medium' : 'font-medium'}>
                    {shipping === 0 ? t('cart.free') : `$${shipping.toFixed(2)}`}
                  </span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>{t('cart.tax')}</span>
                  <span className="font-medium">${tax.toFixed(2)}</span>
                </div>
                {cartTotal < 100 && (
                  <p className="text-xs text-amber-600 bg-amber-50 rounded-lg p-2">
                    {t('cart.addMoreForFreeShipping', { amount: `$${(100 - cartTotal).toFixed(2)}` })}
                  </p>
                )}
                <div className="border-t border-amber-100 pt-3 flex justify-between font-bold text-lg text-gray-800">
                  <span>{t('cart.total')}</span>
                  <span className="text-amber-800">${total.toFixed(2)}</span>
                </div>
              </div>
              <Link to="/checkout">
                <Button className="w-full">{t('cart.proceedCheckout')}</Button>
              </Link>
              <Link to="/products">
                <button className="w-full mt-3 text-sm text-amber-700 hover:text-amber-900 transition-colors text-center py-2">
                  {t('cart.continueShopping')}
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
